import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type CalendarEvent = {
  uid: string;
  summary: string;
  description?: string;
  start: Date;
  end: Date;
};

interface CalendarContextType {
  events: CalendarEvent[];
  disabledDatesSet: Set<string>;
  isLoading: boolean;
  error: string | null;
  isDateDisabled: (date: Date) => boolean;
}

const CalendarContext = createContext<CalendarContextType | undefined>(undefined);

const CACHE_KEY = 'ty-gwennili-calendar-cache';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface CachedData {
  events: { uid: string; summary: string; description?: string; start: string; end: string }[];
  disabledDates: string[];
  timestamp: number;
}

function parseICalData(icalData: string): CalendarEvent[] {
  const events: CalendarEvent[] = [];
  const lines = icalData.split('\n').map(line => line.trim());
  
  let currentEvent: Partial<CalendarEvent> | null = null;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    if (line === 'BEGIN:VEVENT') {
      currentEvent = {};
    } else if (line === 'END:VEVENT' && currentEvent) {
      if (currentEvent.uid && currentEvent.start instanceof Date && currentEvent.end instanceof Date) {
        events.push(currentEvent as CalendarEvent);
      }
      currentEvent = null;
    } else if (currentEvent) {
      if (line.startsWith('UID:')) {
        currentEvent.uid = line.substring(4);
      } else if (line.startsWith('SUMMARY:')) {
        currentEvent.summary = line.substring(8);
      } else if (line.startsWith('DESCRIPTION:')) {
        currentEvent.description = line.substring(12);
      } else if (line.startsWith('DTSTART;VALUE=DATE:')) {
        const parsedDate = parseICalDate(line.substring(19));
        if (parsedDate) currentEvent.start = parsedDate;
      } else if (line.startsWith('DTSTART:')) {
        const parsedDate = parseICalDate(line.substring(8));
        if (parsedDate) currentEvent.start = parsedDate;
      } else if (line.startsWith('DTEND;VALUE=DATE:')) {
        const parsedDate = parseICalDate(line.substring(17));
        if (parsedDate) currentEvent.end = parsedDate;
      } else if (line.startsWith('DTEND:')) {
        const parsedDate = parseICalDate(line.substring(6));
        if (parsedDate) currentEvent.end = parsedDate;
      }
    }
  }
  
  return events;
}

function parseICalDate(dateStr: string): Date | null {
  try {
    if (!dateStr || dateStr.length < 8) return null;
    
    const cleanDateStr = dateStr.replace(/[TZ]/g, '');
    
    const year = parseInt(cleanDateStr.substr(0, 4));
    const month = parseInt(cleanDateStr.substr(4, 2)) - 1;
    const day = parseInt(cleanDateStr.substr(6, 2));
    
    if (isNaN(year) || isNaN(month) || isNaN(day) || 
        year < 2000 || year > 2100 || month < 0 || month > 11 || day < 1 || day > 31) {
      return null;
    }
    
    const resultDate = new Date(year, month, day);
    resultDate.setHours(0, 0, 0, 0);
    return resultDate;
  } catch {
    return null;
  }
}

function generateDisabledDatesSet(events: CalendarEvent[]): Set<string> {
  const disabledDates = new Set<string>();
  
  events.forEach(event => {
    if (!(event.start instanceof Date) || !(event.end instanceof Date)) return;
    
    const startDate = new Date(event.start);
    const endDate = new Date(event.end);
    
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    endDate.setDate(endDate.getDate() - 1);
    
    let currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const dateKey = `${currentDate.getFullYear()}-${currentDate.getMonth()}-${currentDate.getDate()}`;
      disabledDates.add(dateKey);
      currentDate.setDate(currentDate.getDate() + 1);
    }
  });
  
  return disabledDates;
}

function getDateKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

export function CalendarProvider({ children }: { children: ReactNode }) {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [disabledDatesSet, setDisabledDatesSet] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchCalendarData() {
      try {
        // Check localStorage cache first
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const cachedData: CachedData = JSON.parse(cached);
          const now = Date.now();
          
          if (now - cachedData.timestamp < CACHE_TTL) {
            // Restore from cache
            const restoredEvents = cachedData.events.map(e => ({
              ...e,
              start: new Date(e.start),
              end: new Date(e.end)
            }));
            setEvents(restoredEvents);
            setDisabledDatesSet(new Set(cachedData.disabledDates));
            setIsLoading(false);
            return;
          }
        }

        setIsLoading(true);
        
        // Call the Edge Function
        const { data, error: fetchError } = await supabase.functions.invoke('fetch-ical');
        
        if (fetchError) {
          throw new Error(fetchError.message);
        }

        const icalData = data as string;
        const parsedEvents = parseICalData(icalData);
        const disabledSet = generateDisabledDatesSet(parsedEvents);
        
        setEvents(parsedEvents);
        setDisabledDatesSet(disabledSet);
        setError(null);
        
        // Cache to localStorage
        const cacheData: CachedData = {
          events: parsedEvents.map(e => ({
            uid: e.uid,
            summary: e.summary,
            description: e.description,
            start: e.start.toISOString(),
            end: e.end.toISOString()
          })),
          disabledDates: Array.from(disabledSet),
          timestamp: Date.now()
        };
        localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
        
      } catch (err) {
        console.error("Error fetching calendar:", err);
        setError(err instanceof Error ? err.message : "Erreur inconnue");
      } finally {
        setIsLoading(false);
      }
    }

    fetchCalendarData();
    
    // Refresh every hour
    const intervalId = setInterval(fetchCalendarData, 60 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, []);

  const isDateDisabled = (date: Date): boolean => {
    return disabledDatesSet.has(getDateKey(date));
  };

  return (
    <CalendarContext.Provider value={{ events, disabledDatesSet, isLoading, error, isDateDisabled }}>
      {children}
    </CalendarContext.Provider>
  );
}

export function useCalendar() {
  const context = useContext(CalendarContext);
  if (context === undefined) {
    throw new Error('useCalendar must be used within a CalendarProvider');
  }
  return context;
}
